import { MutationType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import isNumber from 'lodash/isNumber';
import { BaseMutation } from '../base/base-mutation';
import { DataType } from '../../data';
import { invert } from '../../utils/protoutil';
import { Rule } from '../base/mutation-rules';
import { shallowOmit } from '../../utils/object-utils';
import { cloneDeep } from '../../utils/clone-deep';
import type {
    ICTTextListStyleDelta,
    ICTTextBody,
    ICTTextBodyPropertiesDelta,
    ICTTextBodyDelta,
} from '@tencent/slide-sdk-pb-model';
import type { DeepReadonly } from 'ts-essentials';
import type { MaybeError } from '../base/base-mutation';
import type { IValidSetTextBodyPropertiesMutationData } from '../base/valid-mutation-data';
import type { PresentationDataModel } from '../../model/presentation-data-model';

export type ITextBodyPropertiesDelta = {
    col?: number;
    row?: number;
    textBodyProperties?: ICTTextBodyPropertiesDelta | null;
    textListStyle?: ICTTextListStyleDelta | null;
};

export class TextBodyPropertiesUtils {
    static fromMutationData(
        data: DeepReadonly<Omit<IValidSetTextBodyPropertiesMutationData, 'type'>>,
    ): DeepReadonly<ITextBodyPropertiesDelta> {
        return shallowOmit(data, 'type', 'pageId', 'shapeId');
    }

    static toTextBodyDelta(delta: ITextBodyPropertiesDelta): ICTTextBodyDelta {
        const { textBodyProperties, textListStyle } = delta;
        return { bodyPr: textBodyProperties, lstStyle: textListStyle };
    }

    static getTextBodyProperties(
        presentation: PresentationDataModel,
        shapeId: string,
        col?: number,
        row?: number,
    ): DeepReadonly<ICTTextBody> {
        const shape = presentation.store.models.get(shapeId, DataType.Shape, DataType.GraphicalObjectFrameTable);
        assert(shape, 'shape should exist');
        if (shape.type === DataType.Shape) {
            const { bodyPr, lstStyle } = shape.store.data.txBody || {};
            return { bodyPr, lstStyle };
        }
        if (!isNumber(col) || !isNumber(row)) return {};
        const cell = shape.getOwnedData().graphic?.graphicData?.tbl?.tr?.[row]?.tc?.[col];
        assert(cell, 'cell should exist');
        const { bodyPr, lstStyle } = cell.txBody || {};
        return { bodyPr, lstStyle };
    }

    static fromTextBodyProperties(
        data: DeepReadonly<ICTTextBodyDelta>,
        pageId: string,
        shapeId: string,
        col = 0,
        row = 0,
    ): IValidSetTextBodyPropertiesMutationData {
        const textBodyPropertiesMutationData: IValidSetTextBodyPropertiesMutationData = {
            type: MutationType.SetTextBodyProperties,
            pageId,
            shapeId,
            col,
            row,
        };
        if (data.bodyPr === null) textBodyPropertiesMutationData.textBodyProperties = null;
        else if (data.bodyPr !== undefined) textBodyPropertiesMutationData.textBodyProperties = cloneDeep(data.bodyPr);
        if (data.lstStyle === null) textBodyPropertiesMutationData.textListStyle = null;
        else if (data.lstStyle !== undefined) textBodyPropertiesMutationData.textListStyle = cloneDeep(data.lstStyle);
        return textBodyPropertiesMutationData;
    }
}

export class SetTextBodyPropertiesMutation extends BaseMutation<
    PresentationDataModel,
    IValidSetTextBodyPropertiesMutationData
> {
    public readonly type: MutationType = MutationType.SetTextBodyProperties;

    public apply(presentation: PresentationDataModel) {
        const { shapeId, col, row } = this.data;
        const shape = presentation.store.models.get(shapeId, DataType.Shape, DataType.GraphicalObjectFrameTable);
        assert(shape, 'shape should exist');
        if (shape.type === DataType.Shape) {
            const deltaData = TextBodyPropertiesUtils.toTextBodyDelta(this.data);
            shape.store.setTextBodyProperties(deltaData);
            return;
        }
        if (shape.type === DataType.GraphicalObjectFrameTable && isNumber(col) && isNumber(row)) {
            const deltaData = TextBodyPropertiesUtils.toTextBodyDelta(this.data);
            shape.store.setTextBodyProperties(deltaData, col, row);
            return;
        }
    }

    public invert(presentation: PresentationDataModel): [IValidSetTextBodyPropertiesMutationData] {
        const { row, col, shapeId, pageId } = this.data;
        const mutationData = TextBodyPropertiesUtils.fromTextBodyProperties(
            TextBodyPropertiesUtils.getTextBodyProperties(presentation, shapeId, col, row),
            pageId,
            shapeId,
            row,
            col,
        );
        const snapshot: DeepReadonly<ITextBodyPropertiesDelta> = {
            textBodyProperties: mutationData.textBodyProperties,
            textListStyle: mutationData.textListStyle,
            col,
            row,
        };
        return [
            {
                type: MutationType.SetTextBodyProperties,
                pageId,
                shapeId,
                ...invert(snapshot, TextBodyPropertiesUtils.fromMutationData(this.data)),
            },
        ];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, shapeId, col, row } = this.data;
        return Rule.every(
            Rule.shape.should.existInPage(presentation, shapeId, pageId),
            Rule.shape.should.hasTextBody(presentation, shapeId, col, row),
        );
    }
}
